import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import useMediaQuery from "hooks/useMediaQuery";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import Header from "../../components/Header";
import ProjectApi from "../../services/api/projects";
import { tokens } from "../../theme";
import ProjectForm from "./form";

const Projects = () => {
  const navigate = useNavigate();

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const projectModule = ProjectApi();
  const [projectsList, setProjectsList] = useState([]);

  const [isConfirmationOpen, setConfirmationOpen] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [editingData, setEditingData] = useState();

  const [open, setOpen] = useState(false);

  const isSmallScreen = useMediaQuery("(max-width: 767px)");

  const columns = [
    { field: "id", headerName: "ID" },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
    },
    {
      field: "",
      headerName: "Edit",
      width: 200,
      renderCell: ({ row: { id } }) => {
        return (
          <Box
            display="flex"
            gap={2}
            justifyContent="center"
            borderRadius="4px"
          >
            <Box display="flex">
              <EditOutlinedIcon
                fontSize="small"
                style={{ marginRight: "4px" }}
              />
              <Typography
                variant="button"
                color="warning"
                onClick={() => editProject(id)}
                style={{ cursor: "pointer", marginRight: "10px" }}
              >
                Edit
              </Typography>
            </Box>

            <Box display="flex">
              <DeleteOutlineOutlinedIcon
                fontSize="small"
                color="error"
                style={{ marginRight: "4px" }}
              />
              <Typography
                variant="button"
                color="error"
                onClick={() => deleteProject(id)}
                style={{ cursor: "pointer" }}
              >
                Delete
              </Typography>
            </Box>
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const data = await projectModule.getAll();

      setProjectsList(data.results);
    } catch (error) {
      console.log(fetchErrorMsg, error);
    }
  };

  const editProject = (id) => {
    const project = projectsList.find((row) => row.id === id);
    setEditingData(project);
    viewForm();
  };

  const deleteProject = (id) => {
    setSelectedId(id);
    handleOpenConfirmation();
  };

  const handleOpenConfirmation = () => {
    setConfirmationOpen(true);
  };

  const handleCloseConfirmation = () => {
    setConfirmationOpen(false);
  };

  const handleConfirmAction = () => {
    try {
      const response = projectModule.destroy(selectedId);

      console.log(deletedSuccessMsg, response);
      fetchData();
    } catch (error) {
      console.log(deleteErrorMsg, error);
    }

    handleCloseConfirmation();
  };

  const createProject = () => {
    setEditingData();
    viewForm();
  };

  const viewForm = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    fetchData();
  };

  return (
    <>
      <Box
        gap={2}
        sx={{
          display: "flex",
          alignItems: "center",
          padding: "15px 30px",
          borderBottom: "1px solid var(--color-primary-200)",
        }}
      >
        <Header title="Projects" />
        <Box sx={{ borderLeft: "1px solid var(--color-primary-200)" }}>
          <Button
            type="submit"
            variant="text"
            onClick={createProject}
            sx={{ padding: "5px 15px" }}
            startIcon={<AddIcon />}
          >
            Create Project
          </Button>
        </Box>
      </Box>

      <Box m="30px" display="flex" flexDirection="column" flex={1}>
        <Box
          sx={{
            height: {
              xs: "100%",
              sm: 300,
              md: "calc(100vh - 65px - 50px - 60px)",
            },
          }}
        >
          <DataGrid
            rows={projectsList}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            columnHeaderHeight={isSmallScreen ? 70 : 60}
            sx={{
              "& .MuiDataGrid-columnHeader--moving": {
                backgroundColor: "inherit !important",
              },
              ...(isSmallScreen && {
                "& .MuiDataGrid-columnSeparator": {
                  display: "none",
                },
                "& .MuiDataGrid-sortIcon": {
                  display: "none",
                },
                "& .MuiDataGrid-menuIcon": {
                  display: "none",
                },
                "& .MuiDataGrid-columnHeader": {
                  padding: "4px !important",
                  justifyContent: "center",
                  alignItems: "center",
                },
                "& .MuiDataGrid-columnHeaderTitleContainer": {
                  padding: "0 !important",
                  margin: "0 !important",
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  whiteSpace: "normal !important",
                  wordBreak: "normal",
                  textAlign: "center",
                  lineHeight: 1.2,
                },
              }),
            }}
          />
        </Box>
      </Box>

      <ConfirmationDialog
        open={isConfirmationOpen}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmAction}
      />

      <ProjectForm
        formData={editingData}
        open={open}
        handleClose={() => handleClose()}
      />
    </>
  );
};

const deletedSuccessMsg = "Project deleted successfully: ";
const deleteErrorMsg = "Not deleted project. ";
const fetchErrorMsg = "Not loading users";

export default Projects;
